import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 4000);
    }
  };

  return (
    <footer className="bg-stone-50 border-t border-stone-100">
      {/* Top CTA Strip */}
      <div className="border-b border-stone-100">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <p className="text-amber-600 text-xs font-bold uppercase tracking-widest mb-2">Stay Connected</p>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-stone-900">
                Get exclusive offers &amp; updates
              </h3>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-3 w-full lg:w-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="flex-1 lg:w-72 bg-white border border-stone-200 rounded-full px-5 py-3 text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:border-amber-400 transition-colors"
              />
              <button
                type="submit"
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                Subscribe
              </button>
            </form>
            {subscribed && (
              <p className="text-green-600 text-sm font-medium">Thank you for subscribing!</p>
            )}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 overflow-hidden flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/3301efb4abc894ce1097fc48d2d46708/299f592c2e3e9cf1636e28f1b3d51d14.png"
                  alt="Faasley Foods"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <p className="text-stone-400 text-sm leading-relaxed">
              Faasley Foods Pvt. Ltd. — crafting premium Indian packaged food products with authentic taste, hygienic packaging, and integrity in every decision.
            </p>
            <p className="text-amber-600 text-sm italic font-medium">"Integrity Shapes Our Flavours"</p>
            <div className="flex items-center gap-2.5 pt-1">
              {[
                { icon: 'ri-instagram-line', label: 'Instagram', href: '#' },
                { icon: 'ri-facebook-circle-line', label: 'Facebook', href: '#' },
                { icon: 'ri-whatsapp-line', label: 'WhatsApp', href: 'https://wa.me/918800889959' },
                { icon: 'ri-linkedin-box-line', label: 'LinkedIn', href: '#' },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="nofollow noreferrer"
                  aria-label={social.label}
                  className="w-9 h-9 rounded-full border border-stone-200 bg-white flex items-center justify-center text-stone-400 hover:bg-amber-500 hover:border-amber-500 hover:text-white transition-all cursor-pointer"
                >
                  <i className={`${social.icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Our Brands */}
          <div>
            <h4 className="text-stone-900 font-semibold text-sm uppercase tracking-widest mb-5">Our Brands</h4>
            <ul className="space-y-3">
              {[
                { path: '/products?category=Makhana', label: 'Fassnut – Makhana' },
                { path: '/products?category=Snacks', label: 'Fassfalli – Dry Fruits' },
                { path: '/products?category=Spices', label: 'Rajsip – Spices' },
                { path: '/products?category=Tea', label: 'Faastea / Booston Tea' },
                { path: '/products?category=Pickles', label: 'Sachar – Mera Achar' },
                { path: '/products?category=Water', label: 'Watler – Drinking Water' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-stone-400 text-sm hover:text-amber-600 transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-amber-500 transition-all"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-stone-900 font-semibold text-sm uppercase tracking-widest mb-5">Company</h4>
            <ul className="space-y-3">
              {[
                { path: '/about', label: 'About Faasley' },
                { path: '/investors', label: 'Investor Relations' },
                { path: '/contact', label: 'Contact Us' },
                { path: '/faq', label: 'FAQs' },
                { path: '/terms', label: 'Terms & Conditions' },
                { path: '/privacy', label: 'Privacy Policy' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-stone-400 text-sm hover:text-amber-600 transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-amber-500 transition-all"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-stone-900 font-semibold text-sm uppercase tracking-widest mb-5">Contact</h4>
            <ul className="space-y-4">
              {[
                { icon: 'ri-map-pin-line', text: 'Faasleyfoods Pvt. Ltd., India' },
                { icon: 'ri-mail-line', text: 'pari211284@gmail.com' },
                { icon: 'ri-phone-line', text: '+91 8800889959' },
                { icon: 'ri-time-line', text: 'Mon–Sat: 9AM – 6PM IST' },
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-amber-500 text-base`}></i>
                  </div>
                  <span className="text-stone-400 text-sm leading-relaxed">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-200">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-stone-400 text-xs text-center sm:text-left">
              © 2025 Faasleyfoods Pvt. Ltd. All rights reserved. · Crafted with Love &amp; Care
            </p>
            <div className="flex items-center gap-3">
              {['FSSAI', 'Visa', 'Mastercard', 'UPI'].map((item) => (
                <span key={item} className="text-stone-400 text-xs bg-white border border-stone-200 px-2.5 py-1 rounded-md">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
